import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { PERMISSION } from 'constants/logic';
import { rosterPropTypes } from 'constants/propTypes';
import { REPORT_TYPES } from 'features/Stats/Stats.constants';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectUserHasPermission } from 'selectors/general';

import messages from './AttendanceCard.messages';
import { ATTENDANCE_PHASE, countAttendance } from './ScheduleDetailV2.helpers';
import './AttendanceCard.scss';

const { block, element } = bem('attendance-card');

const PHASE_BODY_MESSAGES = {
  [ATTENDANCE_PHASE.MARKING]: messages.markingBody,
  [ATTENDANCE_PHASE.PRE]: messages.preClassBody,
};

function AttendanceOverview({ roster, scheduleId }) {
  const canViewRates = useSelector(state => selectUserHasPermission(state, PERMISSION.DISPLAY_RATE));
  const { attendedCount, missedCount } = countAttendance(roster);

  return (
    <>
      <div className={element('header')}>
        <Message as="h3" className={element('title')} message={messages.attendanceOverview} />
        {canViewRates && (
          <Message
            as={Link}
            className={element('link')}
            message={messages.viewPayoutReport}
            to={makePath.reports({ reportType: REPORT_TYPES.CLASSES, id: scheduleId })}
          />
        )}
      </div>
      <div className={element('counts')}>
        <div className={element('count')}>
          <Isvg className={element('icon', { attended: true })} src={ICONS.checkmark} />
          <Message message={messages.attendedCount} values={{ count: attendedCount }} />
        </div>
        <div className={element('count')}>
          <Isvg className={element('icon', { missed: true })} src={ICONS.cross} />
          <Message message={messages.missedCount} values={{ count: missedCount }} />
        </div>
      </div>
    </>
  );
}

AttendanceOverview.propTypes = {
  roster: rosterPropTypes.isRequired,
  scheduleId: PropTypes.number.isRequired,
};

const AttendanceCard = ({ phase, roster, scheduleId }) => (
  <section className={block()} data-cypress={`attendance-card-${phase}`}>
    {phase === ATTENDANCE_PHASE.POST ? (
      <AttendanceOverview roster={roster} scheduleId={scheduleId} />
    ) : (
      <>
        <Message as="h3" className={element('title')} message={messages.markAttendance} />
        <Message as="p" className={element('body')} message={PHASE_BODY_MESSAGES[phase]} />
      </>
    )}
  </section>
);

AttendanceCard.propTypes = {
  phase: PropTypes.oneOf(Object.values(ATTENDANCE_PHASE)).isRequired,
  roster: rosterPropTypes.isRequired,
  scheduleId: PropTypes.number.isRequired,
};

export default AttendanceCard;
